// Retry a failed load when the situation plausibly changed (issue #3517 item B6).
//
// A song whose notation, MIDI or vocals failed to download used to sit on its error until the
// user reloaded the playlist by hand. The two things that plausibly fix it are the device coming
// back online and the user returning to the app - one retry per event, never polling.
//
// Shared by the sequencer's audio load (sequencer.tsx) and the notation fetch (current_song.tsx).
import { useEffect } from 'react';

// The listener half, free of React so it can be unit tested directly. Returns the unsubscribe.
export function addRetryOnReconnectListeners(retry: () => void): () => void {
  const onOnline = () => {
    retry();
  };
  const onVisibilityChange = () => {
    // coming back to a still-offline app would only fail again
    if (document.visibilityState !== 'visible' || !navigator.onLine) {
      return;
    }
    retry();
  };
  // named listeners with an explicit removeEventListener - the repo's lint rejects the
  // AbortController-signal form
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}

// `retry` must be stable (useCallback), or the listeners are torn down and rebuilt every render.
export function useRetryOnReconnect(enabled: boolean, retry: () => void) {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    return addRetryOnReconnectListeners(retry);
  }, [enabled, retry]);
}
